import { useState } from 'react'
import { Cell, Pie, PieChart, PieLabelRenderProps, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { ChartStep } from './type'

const CHART_HEIGHT = 260
const OUTER_RADIUS = 100
const RING_THICKNESS = 26
const LABEL_OFFSET = 18
const RADIAN = Math.PI / 180
// Below this share an outside label crowds its neighbours; the value is left to the tooltip.
const DONUT_LABEL_MIN_PCT = 5

type Props = {
  steps: readonly ChartStep[]
  /** Integer percentages keyed by step key. */
  values: Record<string, number>
  /** The headline number drawn in the hole, already formatted. */
  centerLabel: string
  formatValue: (value: number) => string
}

type Slice = { key: string; name: string; color: string; value: number }

const START_ANGLE = 90

/** Whether a slice's midpoint sits in the left half of the ring, so its tooltip can open away from the centre label. */
const isOnLeftHalf = (slices: Slice[], index: number) => {
  const slice = slices[index]
  if (!slice) return false

  const total = slices.reduce((sum, { value }) => sum + value, 0)
  const valueBefore = slices.slice(0, index).reduce((sum, { value }) => sum + value, 0)
  const midShare = (valueBefore + slice.value / 2) / total
  const midAngle = START_ANGLE - midShare * 360

  return Math.cos(midAngle * RADIAN) < 0
}

export const DonutChart = ({ steps, values, centerLabel, formatValue }: Props) => {
  const [tooltipOpensLeft, setTooltipOpensLeft] = useState(false)

  // Empty slices are dropped so they draw no gap and get no label.
  const slices: Slice[] = steps
    .map(({ key, name, color }) => ({ key, name, color, value: values[key] ?? 0 }))
    .filter(({ value }) => value > 0)

  const renderLabel = ({ cx, cy, midAngle, outerRadius, index }: PieLabelRenderProps) => {
    const slice = slices[index ?? -1]
    if (!slice || slice.value < DONUT_LABEL_MIN_PCT) return null

    const radius = Number(outerRadius) + LABEL_OFFSET
    const x = Number(cx) + radius * Math.cos(-Number(midAngle) * RADIAN)
    const y = Number(cy) + radius * Math.sin(-Number(midAngle) * RADIAN)

    return (
      <text
        x={x}
        y={y}
        textAnchor={x > Number(cx) ? 'start' : 'end'}
        dominantBaseline="central"
        fill={theme.color.greyBlue}
        fontFamily={theme.font.body}
        fontSize={theme.fontSize.xsmall}
      >
        {formatValue(slice.value)}
      </text>
    )
  }

  const ariaLabel = [centerLabel, ...slices.map(({ name, value }) => `${name} ${formatValue(value)}`)].join(', ')

  return (
    <Wrapper role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <PieChart>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="name"
            startAngle={START_ANGLE}
            endAngle={START_ANGLE - 360}
            innerRadius={OUTER_RADIUS - RING_THICKNESS}
            outerRadius={OUTER_RADIUS}
            stroke={theme.color.white}
            strokeWidth={2}
            label={renderLabel}
            labelLine={false}
            isAnimationActive={false}
            onMouseEnter={(_, index) => setTooltipOpensLeft(isOnLeftHalf(slices, index))}
          >
            {slices.map(({ key, color }) => (
              <Cell key={key} fill={color} />
            ))}
          </Pie>
          <Tooltip
            content={<SliceTooltip formatValue={formatValue} />}
            reverseDirection={{ x: tooltipOpensLeft }}
            wrapperStyle={{ zIndex: 1 }}
          />
        </PieChart>
      </ResponsiveContainer>
      <CenterLabel aria-hidden>{centerLabel}</CenterLabel>
    </Wrapper>
  )
}

const SliceTooltip = ({
  active,
  payload,
  formatValue,
}: TooltipProps<number, string> & { formatValue: (value: number) => string }) => {
  const item = payload?.[0]
  if (!active || !item || typeof item.value !== 'number') return null

  return (
    <TooltipBox>
      {item.name}: {formatValue(item.value)}
    </TooltipBox>
  )
}

const Wrapper = styled.div`
  position: relative;
`

const CenterLabel = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: ${theme.font.heading};
  font-size: ${theme.fontSize.jumbo}px;
  font-weight: 500;
  line-height: 1;
  color: ${theme.color.plum};
  pointer-events: none;
`

const TooltipBox = styled.div`
  padding: ${theme.spacing.xsmall}px ${theme.spacing.small}px;
  border-radius: 8px;
  background-color: ${theme.color.black};
  color: ${theme.color.white};
  font-family: ${theme.font.body};
  font-size: ${theme.fontSize.xsmall}px;
  line-height: 18px;
  white-space: nowrap;
`
